import { FormInstance } from 'antd'
import { useCallback, useRef, useState } from 'react'
import { useAuth } from '../../../auth'
import { ChatMessageProps } from '../../../components/chat/chat'
import { useAssistantMessage } from '../../../prompts/hooks'
import { StopAndRateConversationResult } from '../../../prompts/output/stop-and-rate-conversation-result'
import { ToolActivity, ToolTestMode } from '../../../app-connection/types'
import { StoreInput } from '../../input/store-input'
import { getCampaignAgentConfig } from './campaign-agent-config'

type UseCreateTryOutConversationProps = {
    form: FormInstance<StoreInput>
    agentConfig?: ReturnType<typeof getCampaignAgentConfig>
    toolTestMode: ToolTestMode
}

type AssistantReply = NonNullable<ReturnType<typeof useAssistantMessage>['data']>

function assistantMessage(content: string): ChatMessageProps {
    return { role: 'assistant', type: 'text', mediaUrl: null, content, createdAt: new Date() }
}

// The test conversation in the create wizard. Responses that arrive after a reset are ignored.
export function useCreateTryOutConversation({ form, agentConfig, toolTestMode }: UseCreateTryOutConversationProps) {
    const { user } = useAuth()

    const [messages, setMessages] = useState<ChatMessageProps[]>([])
    const [conversationLanguage, setConversationLanguage] = useState<string | null>()
    const [match, setMatch] = useState<StopAndRateConversationResult>()
    const [toolActivities, setToolActivities] = useState<ToolActivity[]>([])
    const revision = useRef(0)

    const assistantMessageMutation = useAssistantMessage()

    const reset = useCallback(() => {
        revision.current++
        setToolActivities((previous) => (previous.length ? [] : previous))
        setMessages((previous) => (previous.length ? [] : previous))
        setConversationLanguage(undefined)
        setMatch(undefined)
    }, [])

    const requestReply = (
        history: ChatMessageProps[],
        language: string | null | undefined
    ): Promise<AssistantReply | undefined> | undefined => {
        if (!agentConfig || !user) {
            return undefined
        }

        const requestRevision = revision.current

        return assistantMessageMutation
            .mutateAsync({
                ...agentConfig.buildAssistantInput(form, history),
                name: user.name,
                conversationLanguage: language,
                toolTestMode,
                toolTurnId: crypto.randomUUID(),
            })
            .then((result) => {
                if (requestRevision !== revision.current) {
                    return undefined
                }

                setToolActivities((previous) => [...previous, ...result.toolActivities])
                setConversationLanguage(result.conversationLanguage)

                return result
            })
    }

    const sendAssistantMessage = async (content?: string) => {
        let history = messages

        if (content) {
            history = [...messages, { role: 'user', type: 'text', mediaUrl: null, content, createdAt: new Date() }]
            setMessages(history)
        }

        const result = await requestReply(history, conversationLanguage)

        if (!result) {
            return
        }

        setMessages((previous) => [...previous, assistantMessage(result.message)])

        if ('matchScore' in result) {
            setMatch(result)
        }
    }

    const bootstrap = () => {
        reset()

        void requestReply([], null)?.then((result) => {
            if (result) {
                setMessages([assistantMessage(result.message)])
            }
        })
    }

    return {
        messages,
        conversationLanguage,
        match,
        toolActivities,
        isSending: assistantMessageMutation.isPending,
        sendAssistantMessage,
        bootstrap,
        reset,
    }
}
